import { useEffect, useMemo } from 'react';

import { WorkspaceDTO } from '@gearbelt/global';
import { zodResolver } from '@hookform/resolvers/zod';
import { Countries, IGetWorkspaceSwitcherResponseDto, Products, SelectorTypes, TimezoneMapping } from '@site-mate/sitemate-global-shared';
import { SitemateRegions } from '@site-mate/sitemate-infra-shared/models';
import { WorkspaceUrlService } from '@site-mate/sitemate-infra-shared/services';
import { useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import dayjs from 'dayjs';
import { PhoneNumberUtil } from 'google-libphonenumber';
import { HTTPError } from 'ky';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { z } from 'zod';

import { useToast } from '@/components/ui/toaster';
import { ApiService } from '@/services/api-v2';
import { workspaceService } from '@/services/global-api/workspace.service';
import { useSignUpReferralStore } from '@/stores/signup-referral.store';
import { useSignUpStore } from '@/stores/signup.store';
import { useUserStore } from '@/stores/user.store';
import { useWorkspaceSetupStore, WorkspaceSetupSteps } from '@/stores/workspace-setup.store';
import { getCrossAccountTargetAccountId } from '@/utils/cross-account-setup';
import { toInfraProduct } from '@/utils/product';

import { CROSS_ACCOUNT_DENIED_REASON } from '../no-workspace-available';
import { BusinessAttributesSelectorStep } from '../signup/business-attributes/business-attributes-selector-step';
import { SignUpSidebarBackground } from '../signup/signup-sidebar-background';
import { WorkspaceSetupNameModal } from './workspace-setup-name-modal';
import { WorkspaceSetupFormData, workspaceSetupFormFormSchema } from './workspace-setup.utils';

export const WorkspaceSetupPage = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { user, org, setOrg, setUser } = useUserStore();
  const { workspaceSetupStep, setWorkspaceSetupStep } = useWorkspaceSetupStore();
  const { utmMetadata } = useSignUpStore();
  const { signUpReference, referenceSmsAccountId, clearSignUpReferral } = useSignUpReferralStore();

  const workspaceUrlService = useMemo(() => new WorkspaceUrlService(), []);

  const targetAccountId = getCrossAccountTargetAccountId({
    pathname: window.location.pathname,
    search: window.location.search,
    storedReferenceSmsAccountId: referenceSmsAccountId,
    currentOrgAccountId: org?.accountId,
  });

  const {
    data: userWorkspaces,
    isPending,
    isError,
    error: fetchError,
  } = useQuery({
    queryKey: ['workspaces-for-regular-user', user?.id],
    queryFn: () => workspaceService.getWorkspacesForRegularUser(),
    enabled: !!user && !!targetAccountId,
    staleTime: Infinity,
    retry: false,
  });

  useEffect(() => {
    // Wait until App.tsx has finished bootstrapping the current user before deciding which branch to take
    if (!user) return;

    if (!targetAccountId) {
      if (org) {
        navigate('/dashboard');
      } else {
        setWorkspaceSetupStep(WorkspaceSetupSteps.WorkspaceName);
      }
      return;
    }

    if (isError) {
      const status = axios.isAxiosError(fetchError) ? fetchError.response?.status : undefined;
      console.error('[WorkspaceSetupPage] Failed to check user workspaces for referenced account', {
        targetAccountId,
        status,
        error: fetchError instanceof Error ? fetchError.message : String(fetchError),
      });
      toast.error('Could not verify your account access. Please refresh and try again.');
      return;
    }

    if (!userWorkspaces) return;

    const userWorkspacesInTargetAccount = userWorkspaces.filter((ws) => ws.accountId === targetAccountId);

    // Not a member of the target account — route them away before they fill out a form
    // they can't submit. Backend's userBelongsToAccount check is the authoritative guard.
    //
    // We deliberately do NOT `clearSignUpReferral()` here: doing so triggers an immediate
    // Zustand re-render of ProtectedRoute, which sees a cleared store, treats the page as
    // non-cross-account, and races its own <Navigate> against ours — bouncing the user into
    // scenarios like /no-workspace-available → /workspace-setup → happy-path form (for
    // `isNewUser` users). Let the referral persist in sessionStorage; it dies on tab close.
    //
    // We also prefer the user's first existing workspace over /no-workspace-available so the
    // redirect target isn't a page that bounces isNewUser accounts back into this flow.
    if (userWorkspacesInTargetAccount.length === 0) {
      toast.error("You don't have access to create a workspace for this account.");
      const fallbackWorkspaceId = org?.workspaceId ?? user.workspaces?.[0]?.workspaceId;
      if (fallbackWorkspaceId) {
        navigate(`/workspaces/${fallbackWorkspaceId}/dashboard`);
      } else {
        // Truly-new user with no workspaces anywhere — /no-workspace-available normally bounces
        // `isNewUser` accounts straight back to /workspace-setup. Signal an explicit access
        // denial via location state so the page suppresses the bounce and shows the right copy.
        navigate('/no-workspace-available', { state: { reason: CROSS_ACCOUNT_DENIED_REASON } });
      }
      return;
    }

    const accessibleGearbeltWorkspace = userWorkspacesInTargetAccount.find((ws) => ws.product === Products.Gearbelt);
    if (accessibleGearbeltWorkspace) {
      redirectToWorkspace(accessibleGearbeltWorkspace);
      return;
    }

    // Member of the account but no Gearbelt workspace yet → show setup form (happy path)
    setWorkspaceSetupStep(WorkspaceSetupSteps.WorkspaceName);
    // Depend on primitives (`org?.accountId`, `org?.workspaceId`) rather than the `org` object
    // itself — the object reference changes on Zustand hydration even when the underlying values
    // don't, which would re-fire `toast.error` / `navigate` on what's effectively the same state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, org?.accountId, org?.workspaceId, targetAccountId, userWorkspaces, isError]);

  function redirectToWorkspace(workspace: IGetWorkspaceSwitcherResponseDto) {
    const url = workspaceUrlService.getWorkspaceUrl(window.location, {
      id: workspace.workspaceId,
      product: toInfraProduct(workspace.product),
      legacyId: workspace.legacyId ?? undefined,
      region: (workspace.region as SitemateRegions) ?? undefined,
      subdomain: workspace.subdomain ?? undefined,
    });
    window.location.href = `${url}/dashboard`;
  }

  const form = useForm<WorkspaceSetupFormData>({
    resolver: zodResolver(workspaceSetupFormFormSchema),
    defaultValues: {
      workspaceName: '',
      businessAttributes: { [SelectorTypes.Vertical]: null, [SelectorTypes.Role]: null, [SelectorTypes.Discipline]: null },
    },
    mode: 'onChange',
  });

  function getContinentAndTimezone() {
    const timezone = dayjs.tz.guess();
    const userCountry = TimezoneMapping[timezone]?.countries[0]?.code?.toLowerCase();

    let countryFromPhoneNumber: string | undefined;
    if (!userCountry && user?.phone) {
      const phoneUtil = PhoneNumberUtil.getInstance();
      const parsedPhoneNumber = phoneUtil.parseAndKeepRawInput(user.phone);
      countryFromPhoneNumber = phoneUtil.getRegionCodeForNumber(parsedPhoneNumber)?.toLowerCase();
    }
    const countryCode = userCountry || countryFromPhoneNumber || 'OC';
    const userContinent = Countries.find((c) => c.ccode.toLowerCase() === countryCode)?.continentCode ?? 'OC';
    return { timezone, continent: userContinent };
  }

  const { mutateAsync: setupWorkspace } = useMutation({
    mutationFn: (values: z.infer<typeof workspaceSetupFormFormSchema>) => {
      const timezoneAndContinent = getContinentAndTimezone();
      return ApiService.CreateWorkspace({
        ...values,
        signUpReference: signUpReference,
        referenceSmsAccountId: referenceSmsAccountId,
        utmMetadata: utmMetadata,
        ...timezoneAndContinent,
      });
    },

    onSuccess: async (result) => {
      const { workspace } = (await result.json()) as { workspace: WorkspaceDTO };
      if (user) {
        setUser({
          ...user,
          workspaces: [...(user.workspaces ?? []), workspace],
        });
      }
      setOrg({ ...workspace });
      clearSignUpReferral();
      navigate('/dashboard');
      setWorkspaceSetupStep(WorkspaceSetupSteps.WorkspaceBranding);
    },

    onError: async (error: HTTPError) => {
      // 403 means the user is not a member of the referenced Sitemate Start account.
      // Route them somewhere useful (existing Gearbelt workspace or the no-workspace page)
      // rather than showing a raw error on the setup page they can't complete.
      if (error.response.status === 403) {
        clearSignUpReferral();
        toast.error("You don't have access to create a workspace for this account.");
        if (org?.workspaceId) {
          navigate(`/workspaces/${org.workspaceId}/dashboard`);
        } else {
          navigate('/no-workspace-available');
        }
        return;
      }

      const errorMessage = await error.response.json<{ message: string }>().then((json) => json.message);
      toast.error(`Workspace setup error: ${errorMessage}`);
    },
  });

  async function onSubmit() {
    const formHasErrors = Object.keys(form.formState.errors).length > 0;
    if (formHasErrors) {
      throw new Error('Workspace setup error: workspace information incomplete');
    }
    const values = form.getValues();
    await setupWorkspace({ ...values });
  }

  if (!user) return null;
  if (isPending && targetAccountId) return null;

  return (
    <>
      {workspaceSetupStep === WorkspaceSetupSteps.WorkspaceName && (
        <SignUpSidebarBackground className="opacity-0">
          <WorkspaceSetupNameModal
            form={form}
            register={form.register}
            onNextStep={() => setWorkspaceSetupStep(WorkspaceSetupSteps.BusinessAttributesSelector)}
          />
        </SignUpSidebarBackground>
      )}
      {workspaceSetupStep === WorkspaceSetupSteps.BusinessAttributesSelector && (
        <SignUpSidebarBackground>
          <BusinessAttributesSelectorStep<WorkspaceSetupFormData> form={form} register={form.register} onNextStep={() => onSubmit()} />
        </SignUpSidebarBackground>
      )}
    </>
  );
};
